import * as React from 'react';
import { useEffect, useState, useRef } from 'react';
import PdfViewer from 'awesome-pdf-viewer';
import Main from 'awesome-pdf-viewer/dist/layout/Main';
import Zoom from 'awesome-pdf-viewer/dist/toolbar/Zoom';
import Header from 'awesome-pdf-viewer/dist/layout/Header';
import Toolbar from 'awesome-pdf-viewer/dist/toolbar/Toolbar';
import ViewPanel from 'awesome-pdf-viewer/dist/layout/ViewPanel';
import Pagination from 'awesome-pdf-viewer/dist/toolbar/Pagination';
import { PdfSource, SearchResult } from 'awesome-pdf-viewer/dist/viewer/ViewerBase';
import DocViewer, { BMPRenderer, JPGRenderer, MSDocRenderer, PNGRenderer } from '@cyntler/react-doc-viewer';
import '../../../../components/pdf-preview/pdf-preview.style.scss';
import { getFileExtension } from 'src/helper/utils';
import { Preview_SupportedFileExtensions, UnsupportedFileMessage } from 'src/helper/constants';
import { DownloadIcon } from 'src/assets/svg/download-icon';
import UTEClientViewWrapper from 'src/components/ute-client-view-wrapper';
import UTEButton from 'src/components/ute-button';
import { DisplayDownloadFile } from 'src/helper/display-download-file';
import { ClientHubEvents, IClientTracking } from 'src/modules/common/common-types';
import { saveClientTrackingEvent } from 'src/modules/common/common-api';
import { DownloadComponent } from './gtr-preview-modal';
import { AuthContext } from 'src/context/ute-auth-context/auth-provider';
import UTEDocumentSearchBox from 'src/components/ute-document-search-box';
import SearchIcon from 'src/assets/svg/search-icon';

interface FileViewerProps {
	url: string;
	fileName: string;
	searchText?: string;
	onCancel?: () => void;
}

const initialSearchResult: SearchResult = {
	matchCount: 0,
	pagesMatch: [],
};

export const FileViewer: React.FC<FileViewerProps> = (props) => {
	let _viewPanel: any;
	let _toolbar: any;
	const [isDocLoading, setIsDocLoading] = useState(true);
	const { userDetail } = React.useContext(AuthContext);
	const [textToFind, setTextToFind] = useState<string>('');
	const [searchResult, setSearchResult] = useState<SearchResult>(initialSearchResult);
	const [showClearButton, setShowClearButton] = useState<boolean>(true);
	const [searchTextChanged, setSearchTextChanged] = useState<boolean>(false);
	const [searchIndex, setSearchIndex] = useState<number>(0);
	const [isStateChanged, setIsStateChanged] = useState<boolean>(false);
	const searchBoxRef = useRef(null);

	const setIsLoading = () => {
		setIsDocLoading(false);
	};
	useEffect(() => {
		setToolbarReferences();
	}, [props.url]);

	useEffect(() => {
		if (props.searchText && props.searchText.length > 0 && !searchTextChanged) {
			setTextToFind(props.searchText);
		}
	}, []);

	useEffect(() => {}, [searchResult, searchTextChanged, showClearButton, searchIndex, isStateChanged]);

	useEffect(() => {
		if (searchBoxRef.current) {
			searchBoxRef.current?.focus();
		}
	}, [textToFind]);

	const setToolbarReferences = () => {
		_toolbar && _toolbar.setViewerReference(_viewPanel);
		_viewPanel && _viewPanel.setToolbarReference(_toolbar);
	};
	const isSupportedFile = (fileName: string): boolean => {
		const ext = getFileExtension(fileName);
		return Preview_SupportedFileExtensions.includes(ext);
	};
	const NoRenderer = ({}: any) => {
		return <div className='p-3'>No Renderer for: {getFileExtension(props.fileName)}</div>;
	};
	const onFileDownload = () => {
		let displayDownloadFile = new DisplayDownloadFile();
		props.url && props.url.length > 0 && displayDownloadFile.directDownloadWithFileName(props.url, props.fileName);
		let isAuthUser;
		let userEmail;
		if (userDetail.auRegistrationId > 0) {
			isAuthUser = true;
			userEmail = userDetail.auEmailAddress;
		} else {
			isAuthUser = false;
			userEmail = userDetail.emailAddress;
		}

		let clientTracking: IClientTracking = {
			eventId: ClientHubEvents.DocumentDownloadedFromView,
			eventData: {
				emailId: userEmail,
				fileName: props.fileName,
				newEmailId: '',
				oldEmailId: '',
			},
			actedBy: userEmail,
			registrationId: userDetail.id,
			isAuthorizedUser: isAuthUser,
		};
		saveClientTrackingEvent(clientTracking);
	};

	const hasLessHeaderSpaceForDownloadBtn = () => {
		const width = window.innerWidth;
		return width <= 900;
	};

	const DownloadButton = () => {
		return (
			<UTEClientViewWrapper dataTestAuto='19d3e87e-8d97-4530-a126-c630694e2290'>
				<UTEButton
					className='download-btn'
					dataTestAuto='F48A59BC-957B-4DB5-8E8B-08071C2A207F'
					disabled={isDocLoading}
					text={hasLessHeaderSpaceForDownloadBtn() ? '' : 'Download'}
					title='Download'
					icon={<DownloadIcon />}
					onClick={() => {
						onFileDownload();
					}}
				/>
			</UTEClientViewWrapper>
		);
	};

	const PdfSearchInput = () => {
		return (
			<UTEDocumentSearchBox
				ref={searchBoxRef}
				searchText={textToFind}
				placeholderText='Search'
				searchIcon={<SearchIcon />}
				disabled={textToFind.length < 3}
				searchOnClick={true}
				showClearButton={showClearButton}
				totalWidth='269px'
				height='31px'
				maxLength={260}
				onChangeSearchText={onChangeSearchText}
				onSearch={onSearch}
				onClearSearch={onClearSearch}
				navigateToNextSearch={handleNext}
				navigateToPreviousSearch={handlePrevious}
				totalMatchCount={searchResult.matchCount}
				searchIndex={searchIndex}
				className='pdf_search_input_placement'
			/>
		);
	};

	const onChangeSearchText = (newSearchText: string) => {
		const disallowedChars = /[\\/:*?"<>|]/g;
		const sanitizedValue = newSearchText.replace(disallowedChars, '');
		setTextToFind(sanitizedValue);
		setSearchTextChanged(true);
		setShowClearButton(true);
		if (newSearchText.length == 0) {
			onClearSearch();
		}
	};

	const getSearchTextResult = (result: SearchResult) => {
		if (result.matchCount !== searchResult.matchCount || result.pagesMatch.length !== searchResult.pagesMatch.length) {
			setSearchResult(result);
			setIsStateChanged(true);
		}
	};

	const onSearch = () => {
		_viewPanel.resetSearchIndex();
		setSearchResult(initialSearchResult);
		_viewPanel.searchText(textToFind).then((searchResult: SearchResult) => {
			setSearchResult(searchResult);
			setIsStateChanged(true);
		});
	};

	const onClearSearch = () => {
		setSearchResult(initialSearchResult);
		setIsStateChanged(true);
		setTextToFind('');
		setSearchIndex(0), setShowClearButton(false);
		setIsStateChanged(true);
		setSearchTextChanged(true);
		_viewPanel.resetSearchIndex();
		_viewPanel.resetSearchResult();
	};

	const handleNext = () => {
		_viewPanel.handleNext();
	};

	const handlePrevious = () => {
		_viewPanel.handlePrev();
	};

	const getCurrentSearchInex = (index: number) => {
		setSearchIndex(index + 1);
		setIsStateChanged(true);
	};

	// @ts-ignore
	return !isSupportedFile(props.fileName) ? (
		// @ts-ignore
		<>
			<PdfViewer id={'awesome-pdf-viewer'}>
				<Header>
					{/* @ts-ignore */}
					<Toolbar
						ref={(ref: any) => (_toolbar = ref)}
						hideLeftPanel
						hideRightPanel
						isChildComponentUpdated={searchTextChanged || isStateChanged}>
						<Pagination />
						<p>
							<i className='arrow-down'></i>
						</p>
						<Zoom />
						{props.searchText !== undefined && props.searchText.length === 0 ? <DownloadButton /> : <PdfSearchInput />}
					</Toolbar>
				</Header>
				<Main>
					{/* @ts-ignore */}
					{getFileExtension(props.fileName).toLowerCase() !== 'pdf' ? (
						<>
							<div className='page-viewer'>
								<div className='non-supported-file-container'>{UnsupportedFileMessage}</div>
							</div>
						</>
					) : (
						<ViewPanel
							ref={(ref: any) => (_viewPanel = ref)}
							pdfWorkerSource={`${process.env.PUBLIC_URL}/pdf.worker.min.mjs`}
							pdfSource={PdfSource.createFromUrl(props.url)}
							onDocumentLoad={() => {
								setIsLoading();
							}}
							searchText={textToFind.replace(/[-[\]{}()*+?.,\\^$|#]/g, '\\$&')}
							getSearchResultCallBack={getSearchTextResult}
							trackSearchIndex={true}
							getCurrentSearchIndexCallBack={getCurrentSearchInex}
							isNavigateToFirstSearchMatch={true}
							trackPageRendering={true}></ViewPanel>
					)}
				</Main>
			</PdfViewer>
			{props.searchText !== undefined && props.searchText.length > 0 && (
				<div className='pdf-preview-modal-footer'>
					<div>
						<DownloadButton />
					</div>

					<div className='modal-actions'>
						<UTEButton
							className='cancel-btn'
							dataTestAuto='734D0D6D-8ECD-4A31-93AA-E1CB3230CCED'
							disabled={false}
							text='Cancel'
							title='Cancel'
							onClick={() => {
								props.onCancel && props.onCancel();
							}}
						/>

						<UTEButton
							className='confirm-btn'
							dataTestAuto='1DC4FCBB-AB2B-4EC5-9424-2F91DAD38C6A'
							disabled={false}
							text='OK'
							title='OK'
							onClick={() => {
								props.onCancel && props.onCancel();
							}}
						/>
					</div>
				</div>
			)}
		</>
	) : (
		props.url !== '' && (
			<DocViewer
				pluginRenderers={[MSDocRenderer, JPGRenderer, PNGRenderer, BMPRenderer]}
				documents={[
					{
						uri: props.url,
						fileName: props.fileName,
						fileType: getFileExtension(props.fileName),
					},
				]}
				config={{
					header: {
						overrideComponent: DownloadComponent,
					},
					noRenderer: {
						overrideComponent: NoRenderer,
					},
				}}
				style={{ height: 500 }}
			/>
		)
	);
};

export default FileViewer;
